import { Square, Volume2, VolumeX } from 'lucide-react';
import { useAudioReply } from './AudioReplyContext';
import { useT } from '../I18nContext';

/**
 * Composer-footer switch. Two-mode behaviour, all in one button:
 *
 *   Idle:
 *     Reflects the user's stored preference (default OFF). When
 *     ON, every subsequent ask sets `audio_requested: true` and
 *     the assistant turn comes back with audio that auto-plays.
 *     Click flips the preference.
 *
 *   Live (audio currently playing):
 *     Visual flips to ON regardless of preference (so a
 *     voice-driven temporary playback is visible) and the icon
 *     changes to a stop square. Click stops the audio without
 *     touching the preference, so a user with the toggle OFF
 *     ends up back at OFF after the playback ends.
 *
 * Why both behaviours in one control: the alternative is two
 * separate UI elements (preference toggle + stop button) which
 * is more cluttered for the same functionality, and users
 * already trained on this control's location have one less
 * thing to learn.
 */
export function AudioReplyToggle() {
  const { enabled, toggle, isPlaying, stop } = useAudioReply();
  const t = useT();

  // Visual ON when EITHER the user has it on OR a clip is currently
  // playing. The toggle reflects "is audio happening / about to
  // happen?" rather than just the stored preference.
  const visuallyOn = enabled || isPlaying;
  const Icon = isPlaying ? Square : visuallyOn ? Volume2 : VolumeX;

  function handleClick(e: React.MouseEvent<HTMLLabelElement>) {
    if (isPlaying) {
      // The label's default behaviour would also fire onChange on the
      // hidden checkbox and flip preference - we don't want that.
      // preventDefault stops the implicit click → input → onChange
      // chain, leaving us free to just stop().
      e.preventDefault();
      stop();
      return;
    }
    // Default behaviour: let the label trigger the input → onChange
    // → toggle path. (We intentionally don't stop propagation here.)
  }

  const label = isPlaying
    ? t('audio_reply.stop_long')
    : enabled
      ? t('audio_reply.turn_off')
      : t('audio_reply.turn_on');

  return (
    <label
      className="audio-reply-toggle"
      onClick={handleClick}
      title={label}
    >
      <input
        type="checkbox"
        checked={visuallyOn}
        onChange={toggle}
        className="audio-reply-toggle-input"
        aria-label={label}
      />
      <Icon size={14} className="audio-reply-toggle-icon" aria-hidden />
      <span>{isPlaying ? t('audio_reply.stop_short') : t('audio_reply.label')}</span>
    </label>
  );
}
